import * as React from "react";
import styled from "styled-components";
import { PoseGroup } from "react-pose";
import MenuLayer from "../MenuLayer";
import Downshift from "downshift";
import { useSelect } from "@hubper/theme";

import mergeStyles from "../../util/mergeStyles";
import composeRefs from "../../util/composeRefs";
import highlightText from "../../util/highlightText";

import InputContainer from "../../elements/InputContainer";
import SelectMenu from "../../elements/SelectMenu";
import SelectMenuItem from "../../elements/SelectMenuItem";
import Icon from "../../elements/Icon";
import Text from "../../elements/Text";
import FilterInput from "../../elements/FilterInput";

import { animated, useSpring } from "react-spring";

const Wrapper = styled.div`
  display: inline-block;
  position: relative;
`;

const AnimatedIcon = animated(Icon);

type Props = {
  style?: React.CSSProperties;
  className?: string;
  value: string | null;
  options: Array<{ value: string | number; display: string }>;
  onChange: (value: string | number) => void;
  label?: string;
  stretch?: boolean;
  disabled?: boolean;
  small?: boolean;
  error?: string | boolean;
  placeholder?: string;
  filter?: boolean;
  filterPlaceholder?: string;
};

function SelectBox(
  {
    style,
    className,
    value,
    options,
    onChange,
    label,
    stretch,
    disabled,
    error,
    placeholder,
    filter,
    filterPlaceholder,
    small
  }: Props,
  ref: any
) {
  const [isOpen, setOpen] = React.useState(false);
  const [filterValue, setFilterValue] = React.useState("");
  const inputContainerRef = React.useRef<HTMLDivElement>(null);
  const { color, space } = useSelect();

  const arrowStyle = useSpring({
    transform: `rotate(${isOpen ? 180 : 0}deg)`,
    config: { tension: 300 }
  });

  const selectedOption = value
    ? options.find(option => option.value === value)
    : null;

  const filteredOptions = filterValue
    ? options.filter(option =>
        option!.display.toLowerCase().includes(filterValue.toLowerCase())
      )
    : options;

  return (
    <Downshift
      isOpen={isOpen}
      onSelect={item => {
        onChange(item.value);
        setOpen(false);
      }}
      onOuterClick={() => setOpen(false)}
      itemToString={item => (item ? item.value : "")}
    >
      {({
        isOpen,
        getRootProps,
        getMenuProps,
        getItemProps,
        highlightedIndex
      }) => {
        return (
          <Wrapper
            {...getRootProps()}
            style={mergeStyles({ width: stretch ? "100%" : 250 }, style)}
            className={className}
          >
            <InputContainer
              ref={composeRefs(inputContainerRef, ref)}
              label={label}
              stretch={true}
              disabled={disabled}
              error={error}
              small={small}
              focus={isOpen}
              onClick={() => (!disabled ? setOpen(!isOpen) : null)}
              style={{
                cursor: disabled ? "default" : "pointer",
                transition: "0.2s",
                borderBottomLeftRadius: isOpen ? 0 : 3,
                borderBottomRightRadius: isOpen ? 0 : 3,
                borderBottomColor: isOpen ? color(["grey", 1]) : undefined
              }}
            >
              <Text
                fs={-1}
                c={["grey", selectedOption ? 9 : 3]}
                style={{ flex: 1 }}
              >
                {selectedOption ? selectedOption.display : placeholder || ""}
              </Text>
              <AnimatedIcon
                name="dropdown"
                size={22}
                c={["grey", 2]}
                style={arrowStyle}
              />
            </InputContainer>
            <MenuLayer element={inputContainerRef.current}>
              {style => (
                <PoseGroup flipMove={false}>
                  {isOpen && (
                    <SelectMenu
                      key="menu"
                      {...getMenuProps({
                        style
                      })}
                    >
                      {filter && (
                        <div
                          style={{
                            padding: `${space(0)} ${space(2)}`
                          }}
                        >
                          <FilterInput
                            placeholder={filterPlaceholder}
                            value={filterValue}
                            onChange={(evt: any) =>
                              setFilterValue(evt.target.value)
                            }
                          />
                        </div>
                      )}
                      {filteredOptions.map((option, index) => {
                        const isSelected = option.value === value;

                        return (
                          <SelectMenuItem
                            selected={isSelected}
                            highlighted={highlightedIndex === index}
                            key={option.value}
                            {...getItemProps({
                              item: option,
                              index
                            })}
                          >
                            {filterValue
                              ? highlightText(
                                  option.display as string,
                                  filterValue
                                )
                              : option.display}
                          </SelectMenuItem>
                        );
                      })}
                    </SelectMenu>
                  )}
                </PoseGroup>
              )}
            </MenuLayer>
          </Wrapper>
        );
      }}
    </Downshift>
  );
}

export default React.forwardRef(SelectBox);
